import { useEffect, useState, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

// iter250 — Onglets de la page Atelier (bande sur desktop, menu déroulant sur mobile)
const TABS = [
  { value: "unifie", label: "Planning & atelier", icon: CalendarDays },
  { value: "calendrier", label: "Calendrier complet", icon: Calendar },
  { value: "dashboard", label: "Tableau de bord", icon: ClipboardList },
  { value: "catalogue", label: "Catalogue articles", icon: Package },
  { value: "taux", label: "Taux horaires", icon: Calculator },
  { value: "import", label: "Import", icon: Upload, adminOnly: true },
];
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction, AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import {
  Wrench, Package, Calculator, Upload, FileSpreadsheet, Plus, Search,
  Edit, Trash2, Download, Save, Loader2, AlertCircle, CheckCircle2, Info, X,
  ClipboardList, CalendarDays, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AtelierDashboard from "./AtelierDashboard";
import AtelierUnifie from "./AtelierUnifie";
import Calendrier from "./Calendrier";

const CATEGORIE_LABELS = {
  piece: "Pièce",
  fourniture: "Fourniture",
  liquide: "Liquide",
  main_oeuvre: "Main d'œuvre",
  divers: "Divers",
};
const CATEGORIE_COLORS = {
  piece: "bg-blue-50 text-blue-700 border-blue-200",
  fourniture: "bg-cyan-50 text-cyan-700 border-cyan-200",
  liquide: "bg-purple-50 text-purple-700 border-purple-200",
  main_oeuvre: "bg-orange-50 text-orange-700 border-orange-200",
  divers: "bg-gray-50 text-gray-700 border-gray-200",
};
// Groupes pour rendu visuel (Fournitures = Pièces+Fournitures+Liquides ; Main d'œuvre ; Divers)
const CATEGORIE_GROUPS = [
  { groupe: "Fournitures", items: ["piece", "fourniture", "liquide"] },
  { groupe: "Main d'œuvre", items: ["main_oeuvre"] },
  { groupe: "Autres", items: ["divers"] },
];
const UNITES = [
  { value: "piece", label: "pièce" },
  { value: "h", label: "heure" },
  { value: "litre", label: "litre" },
  { value: "kg", label: "kg" },
  { value: "m", label: "mètre" },
  { value: "jeu", label: "jeu" },
  { value: "forfait", label: "forfait" },
];

const TYPE_TARIF_LABELS = {
  interne: "Interne",
  particulier: "Particulier",
  assurance: "Assurance",
  flotte: "Flotte",
};
const CATEGORIES_TAUX = ["mecanique", "carrosserie", "diagnostic", "pneus"];
const CAT_TAUX_LABELS = {
  mecanique: "Mécanique",
  carrosserie: "Carrosserie",
  diagnostic: "Diagnostic",
  pneus: "Pneus",
};

const EMPTY_ARTICLE = {
  reference: "", libelle: "", categorie: "piece", prix_ht: "",
  unite: "piece", tva: 8.1, reference_fournisseur: "", fournisseur: "",
  prix_achat: "", notes: "", actif: true,
  stock_quantite: "", stock_unite: "pcs", stock_seuil_alerte: "",
  categorie_mo: "mecanique",
};

const CATEGORIES_MO = [
  { value: "mecanique", label: "Mécanique" },
  { value: "carrosserie", label: "Carrosserie" },
  { value: "pneus", label: "Pneus" },
  { value: "diagnostic", label: "Diagnostic" },
];

const STOCK_UNITES = [
  { value: "pcs", label: "pièces (pcs)" },
  { value: "L", label: "litres (L)" },
  { value: "kg", label: "kg" },
  { value: "m", label: "mètres" },
  { value: "jeu", label: "jeu" },
];

// iter165 — Taux horaire de référence pour prorata MO forfaitaire (aligné avec core.py côté backend)
export const TAUX_HORAIRE_ATELIER_REFERENCE_HT = 140.0;

function ArticleDialog({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState(EMPTY_ARTICLE);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing ? { ...EMPTY_ARTICLE, ...editing } : EMPTY_ARTICLE);
    }
  }, [open, editing]);

  const submit = async () => {
    if (!form.libelle.trim()) {
      toast.error("Le libellé est obligatoire");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        prix_ht: parseFloat(form.prix_ht) || 0,
        tva: parseFloat(form.tva) || 8.1,
        prix_achat: form.prix_achat ? parseFloat(form.prix_achat) : null,
        reference: form.reference || null,
        reference_fournisseur: form.reference_fournisseur || null,
        fournisseur: form.fournisseur || null,
        notes: form.notes || null,
        stock_quantite: form.stock_quantite === "" || form.stock_quantite === null
          ? null
          : parseFloat(form.stock_quantite),
        stock_unite: form.stock_quantite === "" || form.stock_quantite === null
          ? null
          : (form.stock_unite || "pcs"),
        stock_seuil_alerte: form.stock_seuil_alerte === "" || form.stock_seuil_alerte === null
          ? null
          : parseFloat(form.stock_seuil_alerte),
        categorie_mo: form.categorie === "main_oeuvre" ? (form.categorie_mo || "mecanique") : null,
      };
      if (editing) {
        await api.put(`/articles/${editing.id}`, payload);
        toast.success("Article mis à jour");
      } else {
        await api.post("/articles", payload);
        toast.success("Article créé");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? "Modifier l'article" : "Nouvel article"}</DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label>Référence interne</Label>
            <Input value={form.reference || ""} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="HUI-5W30-5L" data-testid="article-reference" />
          </div>
          <div className="md:col-span-2">
            <Label>Libellé *</Label>
            <Input value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} data-testid="article-libelle" />
          </div>
          <div>
            <Label>Catégorie</Label>
            <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
              <SelectTrigger data-testid="article-categorie"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIE_GROUPS.map((g) => (
                  <div key={g.groupe}>
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 px-2 pt-2 pb-1 font-semibold">{g.groupe}</div>
                    {g.items.map((c) => (
                      <SelectItem key={c} value={c}>{CATEGORIE_LABELS[c]}</SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>
          {form.categorie === "main_oeuvre" && (
            <div>
              <Label>Catégorie MO (taux horaire)</Label>
              <Select value={form.categorie_mo || "mecanique"} onValueChange={(v) => setForm({ ...form, categorie_mo: v })}>
                <SelectTrigger data-testid="article-categorie-mo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES_MO.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Prix HT (CHF)</Label>
            <Input type="number" step="0.01" value={form.prix_ht} onChange={(e) => setForm({ ...form, prix_ht: e.target.value })} data-testid="article-prix" />
          </div>
          <div>
            <Label>Unité</Label>
            <Select value={form.unite} onValueChange={(v) => setForm({ ...form, unite: v })}>
              <SelectTrigger data-testid="article-unite"><SelectValue /></SelectTrigger>
              <SelectContent>
                {UNITES.map((u) => (<SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>TVA (%)</Label>
            <Input type="number" step="0.1" value={form.tva} onChange={(e) => setForm({ ...form, tva: e.target.value })} />
          </div>
          <div>
            <Label>Réf. fournisseur</Label>
            <Input value={form.reference_fournisseur || ""} onChange={(e) => setForm({ ...form, reference_fournisseur: e.target.value })} />
          </div>
          <div>
            <Label>Fournisseur</Label>
            <Input value={form.fournisseur || ""} onChange={(e) => setForm({ ...form, fournisseur: e.target.value })} />
          </div>
          <div>
            <Label>Prix achat (CHF)</Label>
            <Input type="number" step="0.01" value={form.prix_achat || ""} onChange={(e) => setForm({ ...form, prix_achat: e.target.value })} />
          </div>
          {/* iter165 — Gestion de stock (optionnelle) */}
          <div className="md:col-span-3 border-t pt-3 mt-1">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Stock (optionnel)</div>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label>Quantité en stock</Label>
                <Input
                  type="number" step="0.01"
                  value={form.stock_quantite ?? ""}
                  onChange={(e) => setForm({ ...form, stock_quantite: e.target.value })}
                  placeholder="Laisser vide = pas de gestion"
                  data-testid="article-stock-quantite"
                />
              </div>
              <div>
                <Label>Unité stock</Label>
                <Select
                  value={form.stock_unite || "pcs"}
                  onValueChange={(v) => setForm({ ...form, stock_unite: v })}
                >
                  <SelectTrigger data-testid="article-stock-unite"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STOCK_UNITES.map((u) => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Seuil d&apos;alerte</Label>
                <Input
                  type="number" step="0.01"
                  value={form.stock_seuil_alerte ?? ""}
                  onChange={(e) => setForm({ ...form, stock_seuil_alerte: e.target.value })}
                  placeholder="ex: 5"
                  data-testid="article-stock-seuil"
                />
              </div>
            </div>
            {form.categorie === "main_oeuvre" && form.unite === "forfait" && (
              <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1.5 mt-2">
                <Info className="w-3.5 h-3.5 inline mr-1" />
                Main d&apos;œuvre forfaitaire : la ligne reste affichée comme un forfait sur l&apos;OR.
                Le temps atelier est comptabilisé en interne (stats) : <b>prix / taux horaire {CATEGORIES_MO.find((c) => c.value === (form.categorie_mo || "mecanique"))?.label}</b> du tarif de l&apos;OR.
              </div>
            )}
          </div>
          <div className="md:col-span-3">
            <Label>Notes</Label>
            <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="article-save">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Enregistrer</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CatalogueTab({ isAdmin }) {
  const [q, setQ] = useState("");
  const [categorie, setCategorie] = useState("");
  const [stockLowOnly, setStockLowOnly] = useState(false);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (q) params.q = q;
      if (categorie) params.categorie = categorie;
      if (stockLowOnly) params.stock_low_only = true;
      const { data: resp } = await api.get("/articles", { params });
      setData(resp);
    } finally {
      setLoading(false);
    }
  }, [q, categorie, stockLowOnly]);

  useEffect(() => {
    const t = setTimeout(fetchData, 200);
    return () => clearTimeout(t);
  }, [fetchData]);

  const onEdit = (a) => { setEditing(a); setDialogOpen(true); };
  const onNew = () => { setEditing(null); setDialogOpen(true); };

  const onDelete = async (a) => {
    if (!window.confirm(`Supprimer "${a.libelle}" ?`)) return;
    try {
      await api.delete(`/articles/${a.id}`);
      toast.success("Article supprimé");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (libellé, réf, fournisseur)…" className="pl-10" data-testid="article-search" />
        </div>
        <Select value={categorie || "all"} onValueChange={(v) => setCategorie(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="article-filter-categorie">
            <SelectValue placeholder="Catégorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes catégories</SelectItem>
            {CATEGORIE_GROUPS.map((g) => (
              <div key={g.groupe}>
                <div className="text-[10px] uppercase tracking-wider text-gray-400 px-2 pt-2 pb-1 font-semibold">{g.groupe}</div>
                {g.items.map((c) => (
                  <SelectItem key={c} value={c}>{CATEGORIE_LABELS[c]}</SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant={stockLowOnly ? "default" : "outline"}
          onClick={() => setStockLowOnly(!stockLowOnly)}
          className={stockLowOnly ? "bg-red-600 hover:bg-red-700" : ""}
          data-testid="article-filter-stock-low"
          title="Afficher uniquement les articles avec stock ≤ seuil d'alerte"
        >
          <AlertCircle className="w-4 h-4 mr-2" /> Stock bas
        </Button>
        {isAdmin && (
          <Button onClick={onNew} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="article-new">
            <Plus className="w-4 h-4 mr-2" /> Nouvel article
          </Button>
        )}
      </div>

      <Card className="border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead>Référence</TableHead>
                <TableHead>Libellé</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead className="text-right">Prix HT</TableHead>
                <TableHead>Unité</TableHead>
                <TableHead className="text-right">Stock</TableHead>
                <TableHead>Fournisseur</TableHead>
                {isAdmin && <TableHead className="w-20"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-8 text-gray-400">Chargement…</TableCell></TableRow>}
              {!loading && data.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-12 text-gray-400">
                    Aucun article. Vous pouvez en créer manuellement ou importer un fichier Excel/CSV.
                  </TableCell>
                </TableRow>
              )}
              {!loading && data.items.map((a) => {
                const hasStock = a.stock_quantite !== null && a.stock_quantite !== undefined;
                const seuil = a.stock_seuil_alerte;
                const low = hasStock && seuil != null && seuil > 0 && a.stock_quantite <= seuil;
                return (
                <TableRow key={a.id} className="hover:bg-gray-50" data-testid={`article-row-${a.id}`}>
                  <TableCell><code className="text-xs bg-gray-50 px-1.5 py-0.5 rounded">{a.reference || "—"}</code></TableCell>
                  <TableCell className="font-medium">{a.libelle}</TableCell>
                  <TableCell><Badge variant="outline" className={CATEGORIE_COLORS[a.categorie]}>{CATEGORIE_LABELS[a.categorie]}</Badge></TableCell>
                  <TableCell className="text-right font-mono">{a.prix_ht.toFixed(2)}</TableCell>
                  <TableCell className="text-xs text-gray-500">{a.unite}</TableCell>
                  <TableCell className="text-right text-xs" data-testid={`article-stock-${a.id}`}>
                    {hasStock ? (
                      <span className={`font-mono ${low ? "text-red-600 font-bold" : "text-gray-700"}`}>
                        {a.stock_quantite}{a.stock_unite ? ` ${a.stock_unite}` : ""}
                        {low && <AlertCircle className="w-3 h-3 inline ml-1 text-red-600" />}
                      </span>
                    ) : <span className="text-gray-300">—</span>}
                  </TableCell>
                  <TableCell className="text-xs text-gray-500">{a.fournisseur || "—"}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => onEdit(a)} data-testid={`article-edit-${a.id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="text-red-600" onClick={() => onDelete(a)} data-testid={`article-delete-${a.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </div>
        {!loading && data.total > 0 && (
          <div className="p-3 border-t border-gray-100 text-xs text-gray-500">
            {data.items.length} article(s) affichés / {data.total} au total
          </div>
        )}
      </Card>

      <ArticleDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={fetchData} editing={editing} />
    </div>
  );
}

function TauxHorairesTab({ isAdmin }) {
  const [taux, setTaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({}); // {tid: value}

  const fetchTaux = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/taux-horaires");
      setTaux(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTaux(); }, [fetchTaux]);

  // Build matrix: cat × type_tarif
  const matrix = {};
  taux.forEach((t) => {
    if (!matrix[t.categorie]) matrix[t.categorie] = {};
    matrix[t.categorie][t.type_tarif] = t;
  });

  const startEdit = (t) => setEditing({ ...editing, [t.id]: t.taux_ht });
  const cancelEdit = (tid) => {
    const { [tid]: _, ...rest } = editing;
    setEditing(rest);
  };
  const saveEdit = async (t) => {
    const newVal = parseFloat(editing[t.id]);
    if (isNaN(newVal) || newVal < 0) {
      toast.error("Valeur invalide");
      return;
    }
    try {
      await api.put(`/taux-horaires/${t.id}`, {
        categorie: t.categorie,
        type_tarif: t.type_tarif,
        taux_ht: newVal,
        actif: t.actif,
      });
      toast.success("Taux mis à jour");
      cancelEdit(t.id);
      fetchTaux();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div>
      <Card className="p-4 mb-4 bg-blue-50/30 border-blue-200">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            Matrice des taux horaires HT (CHF/heure), par catégorie de prestation × type de tarif.
            {isAdmin
              ? " Cliquez sur une valeur pour la modifier."
              : " Réservé à l'administrateur — contactez-le pour toute modification."}
          </div>
        </div>
      </Card>

      <Card className="border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="w-40">Catégorie</TableHead>
                {Object.entries(TYPE_TARIF_LABELS).map(([v, l]) => (
                  <TableHead key={v} className="text-right">{l}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {CATEGORIES_TAUX.map((cat) => (
                <TableRow key={cat}>
                  <TableCell className="font-semibold">{CAT_TAUX_LABELS[cat]}</TableCell>
                  {Object.keys(TYPE_TARIF_LABELS).map((typ) => {
                    const t = matrix[cat]?.[typ];
                    if (!t) return <TableCell key={typ} className="text-right text-gray-300">—</TableCell>;
                    const isEditing = editing[t.id] !== undefined;
                    return (
                      <TableCell key={typ} className="text-right" data-testid={`taux-${cat}-${typ}`}>
                        {isEditing ? (
                          <div className="flex items-center gap-1 justify-end">
                            <Input
                              type="number"
                              step="0.01"
                              value={editing[t.id]}
                              onChange={(e) => setEditing({ ...editing, [t.id]: e.target.value })}
                              className="w-20 h-8 text-right"
                              autoFocus
                              data-testid={`taux-${cat}-${typ}-input`}
                            />
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => saveEdit(t)} data-testid={`taux-${cat}-${typ}-save`}>
                              <Save className="w-3.5 h-3.5 text-green-600" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => cancelEdit(t.id)}>
                              <X className="w-3.5 h-3.5 text-gray-400" />
                            </Button>
                          </div>
                        ) : (
                          <button
                            onClick={() => isAdmin && startEdit(t)}
                            disabled={!isAdmin}
                            className={`font-mono px-2 py-1 rounded ${isAdmin ? "hover:bg-[#52a675]/10 cursor-pointer" : "cursor-default opacity-60"}`}
                            data-testid={`taux-${cat}-${typ}-value`}
                          >
                            {t.taux_ht.toFixed(2)}
                          </button>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <p className="text-xs text-gray-500 mt-3">
        Les taux <strong>interne</strong> servent à calculer la rentabilité (coût) en interne.
        Les taux <strong>particulier / assurance / flotte</strong> sont appliqués aux devis et factures selon le type de client.
      </p>
    </div>
  );
}

function ImportTab() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState(null);

  const doPreview = async () => {
    if (!file) return;
    setPreviewing(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/articles/import/preview", fd);
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setPreviewing(false);
    }
  };

  const doCommit = async () => {
    if (!file) return;
    setCommitting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/articles/import/commit", fd);
      setResult(data);
      toast.success(`${data.inserted} article(s) importé(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setCommitting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/articles/template-excel", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_articles_carmain.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erreur téléchargement");
    }
  };

  return (
    <div>
      <Card className="p-5 mb-4 border-blue-200 bg-blue-50/30">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-sm text-blue-900 mb-1">Import depuis Excel ou CSV</h3>
            <p className="text-xs text-blue-800 mb-2">
              Formats acceptés : <code className="bg-white px-1 rounded">.xlsx</code>, <code className="bg-white px-1 rounded">.csv</code> (séparateur ; ou ,).
              Colonnes reconnues : <strong>libelle (obligatoire)</strong>, reference, categorie, prix_ht, unite, tva, reference_fournisseur, fournisseur, prix_achat, notes.
              <br />
              <span className="text-blue-700">💡 Les lignes avec une <strong>référence</strong> déjà présente dans le catalogue seront ignorées (déduplication).</span>
            </p>
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="import-template-download">
              <Download className="w-3.5 h-3.5 mr-2" /> Télécharger le modèle Excel
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6 border-gray-200 mb-4">
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => { setFile(e.target.files?.[0]); setPreview(null); setResult(null); }}
          className="hidden"
          data-testid="import-file-input"
        />
        <div
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#52a675] hover:bg-[#f0f9f4]"
        >
          <FileSpreadsheet className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          {file ? (
            <>
              <div className="font-semibold text-gray-900">{file.name}</div>
              <div className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB</div>
            </>
          ) : (
            <>
              <div className="text-sm font-semibold text-gray-700">Cliquer pour sélectionner un fichier</div>
              <div className="text-xs text-gray-500 mt-1">Excel (.xlsx) ou CSV (.csv)</div>
            </>
          )}
        </div>

        {file && !preview && (
          <div className="mt-4 flex justify-end">
            <Button onClick={doPreview} disabled={previewing} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="import-preview">
              {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
              Prévisualiser
            </Button>
          </div>
        )}
      </Card>

      {preview && (
        <Card className="p-5 mb-4 border-gray-200" data-testid="import-preview-result">
          <h3 className="font-heading font-semibold text-gray-900 mb-3">Aperçu</h3>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="p-3 bg-gray-50 rounded-md">
              <div className="text-xs text-gray-500 uppercase">Total</div>
              <div className="text-2xl font-bold">{preview.total_rows}</div>
            </div>
            <div className="p-3 bg-green-50 rounded-md">
              <div className="text-xs text-green-700 uppercase">Valides</div>
              <div className="text-2xl font-bold text-green-700">{preview.valid}</div>
            </div>
            <div className="p-3 bg-red-50 rounded-md">
              <div className="text-xs text-red-700 uppercase">Erreurs</div>
              <div className="text-2xl font-bold text-red-700">{preview.errors_count}</div>
            </div>
          </div>

          {preview.errors_sample?.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500" /> Erreurs détectées
              </h4>
              <ul className="text-xs space-y-1 max-h-40 overflow-y-auto bg-red-50 p-3 rounded-md">
                {preview.errors_sample.map((e, i) => (<li key={i}>Ligne {e.row} : {e.error}</li>))}
              </ul>
            </div>
          )}

          {preview.preview?.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-2">5 premiers articles valides</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Référence</TableHead>
                    <TableHead>Libellé</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead className="text-right">Prix HT</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.preview.map((a, i) => (
                    <TableRow key={i}>
                      <TableCell><code className="text-xs">{a.reference || "—"}</code></TableCell>
                      <TableCell>{a.libelle}</TableCell>
                      <TableCell><Badge variant="outline" className={CATEGORIE_COLORS[a.categorie]}>{CATEGORIE_LABELS[a.categorie]}</Badge></TableCell>
                      <TableCell className="text-right font-mono">{a.prix_ht.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setFile(null); setPreview(null); }}>Annuler</Button>
            <Button onClick={doCommit} disabled={committing || preview.valid === 0} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="import-commit">
              {committing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Importer {preview.valid} article(s)
            </Button>
          </div>
        </Card>
      )}

      {result && (
        <Card className="p-5 border-green-200 bg-green-50/50" data-testid="import-result">
          <h3 className="font-heading font-semibold text-green-900 mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" /> Import terminé
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-green-800 uppercase">Importés</div>
              <div className="text-3xl font-bold text-green-700">{result.inserted}</div>
            </div>
            <div>
              <div className="text-xs text-gray-700 uppercase">Ignorés/erreurs</div>
              <div className="text-3xl font-bold text-gray-700">{result.skipped}</div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

export default function AtelierPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("unifie");

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="atelier-page">
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2 mb-1">
        <Wrench className="w-7 h-7 text-[#52a675]" /> Atelier
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Ordres de réparation, catalogue articles et taux horaires
      </p>

      <Tabs value={tab} onValueChange={setTab}>
        {/* iter250 — La bande d'onglets débordait sur tablette/téléphone :
            en dessous de md on passe par un petit menu déroulant. */}
        <div className="md:hidden">
          <Select value={tab} onValueChange={setTab}>
            <SelectTrigger className="h-11" data-testid="atelier-tab-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TABS.filter((t) => !t.adminOnly || isAdmin).map((t) => (
                <SelectItem key={t.value} value={t.value} data-testid={`atelier-tab-option-${t.value}`}>
                  <span className="flex items-center gap-2">
                    <t.icon className="w-4 h-4 text-[#52a675]" /> {t.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TabsList className="hidden md:flex w-full justify-start flex-nowrap">
          {TABS.filter((t) => !t.adminOnly || isAdmin).map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`tab-${t.value}`}>
              <t.icon className="w-4 h-4 mr-2" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="unifie" className="mt-4">
          <AtelierUnifie />
        </TabsContent>

        <TabsContent value="calendrier" className="mt-4">
          <Calendrier embedded />
        </TabsContent>

        <TabsContent value="dashboard" className="mt-4">
          <AtelierDashboard />
        </TabsContent>

        <TabsContent value="catalogue" className="mt-4">
          <CatalogueTab isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="taux" className="mt-4">
          <TauxHorairesTab isAdmin={isAdmin} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="import" className="mt-4">
            <ImportTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
